import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { LogIn } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const DEMOS = [
  { role: "Manager", email: "manager@patria.church" },
  { role: "People Operations", email: "peopleops@patria.church" },
  { role: "Executive Leadership", email: "leadership@patria.church" },
];

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user && user !== false && user !== null) {
    return <Navigate to="/" replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim(), password);
      navigate("/", { replace: true });
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const fillDemo = (demoEmail) => {
    setEmail(demoEmail);
    setPassword("Compass#2026");
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-patria-sage">
      {/* Left brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 overflow-hidden bg-patria-greenDeep">
        <img
          src="/patria-logo.png"
          alt=""
          className="absolute -right-24 top-1/2 -translate-y-1/2 w-[540px] opacity-[0.08] pointer-events-none"
        />
        <div className="relative flex items-center gap-3">
          <img src="/patria-logo.png" alt="Patria Church" className="h-12 w-12 rounded-full bg-patria-surface object-contain" />
          <span className="font-heading text-xl font-semibold text-patria-surface">COMPASS</span>
        </div>
        <div className="relative max-w-md">
          <h1 className="font-heading text-4xl lg:text-5xl font-semibold text-patria-surface leading-tight">
            Performance <span className="text-patria-peach">Growth</span> Program
          </h1>
          <p className="mt-4 text-patria-peachSoft/90 text-lg leading-relaxed">
            Specific gaps. Clear standards. A fair, structured path to improvement.
          </p>
        </div>
        <p className="relative text-patria-surface/60 text-sm">Patria Church · People Operations</p>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <img src="/patria-logo.png" alt="Patria Church" className="h-11 w-11 rounded-full object-contain" />
            <span className="font-heading text-xl font-semibold text-patria-ink">COMPASS</span>
          </div>

          <h2 className="font-heading text-2xl font-semibold text-patria-ink">Sign in</h2>
          <p className="text-patria-muted text-sm mt-1 mb-6">Patria Church Performance Management</p>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div className="space-y-2">
              <Label className="text-patria-ink">Email</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@patria.church"
                required
                data-testid="login-email"
                className="bg-patria-surface border-patria-muted/30 focus-visible:ring-patria-green"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-patria-ink">Password</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                data-testid="login-password"
                className="bg-patria-surface border-patria-muted/30 focus-visible:ring-patria-green"
              />
            </div>
            {error && (
              <p className="text-sm text-patria-terracottaDeep bg-patria-terracotta/10 rounded-lg px-3 py-2" data-testid="login-error">
                {error}
              </p>
            )}
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full rounded-full bg-patria-green hover:bg-patria-greenDeep text-patria-surface"
            >
              <LogIn className="h-4 w-4 mr-2" />
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <div className="mt-8 rounded-xl bg-patria-surface/70 border border-patria-muted/15 p-4">
            <p className="text-xs font-semibold text-patria-muted uppercase tracking-wide mb-2">Demo accounts</p>
            <div className="space-y-1.5">
              {DEMOS.map((d) => (
                <button
                  key={d.email}
                  type="button"
                  onClick={() => fillDemo(d.email)}
                  data-testid={`demo-${d.role.toLowerCase().replace(/[^a-z]/g, "-")}`}
                  className="w-full flex items-center justify-between text-left rounded-lg px-3 py-2 hover:bg-patria-sage transition-colors"
                >
                  <span className="text-sm text-patria-ink">{d.role}</span>
                  <span className="text-xs text-patria-muted">{d.email}</span>
                </button>
              ))}
            </div>
            <p className="text-[11px] text-patria-muted mt-2">Password for all: <span className="font-mono">Compass#2026</span></p>
          </div>
        </div>
      </div>
    </div>
  );
}
